// Shared helpers for a parameter's `info` field.
//
// `info` is a single STRING that round-trips through every layer (DB column, backend SQL,
// GraphQL schema, read query). We pack an optional description + URL into it as a JSON
// string. Keeping it a string is the invariant that avoids the write/read type mismatch
// that can break the template list — never write an object/array into `info`.
//
// One shared parser/serializer is used by BOTH the editor (CustomMultiValuedParameterRow)
// and the execute page (FlexicoUploadComponent) so the two can't drift apart.

export interface ParameterInfo {
    description: string;
    url: string;
}

// Read the `info` string into { description, url }. Tolerates legacy values: a plain
// (non-JSON) string is treated as the description.
export const parseInfoField = (raw: any): ParameterInfo => {
    if (raw == null || raw === '') return { description: '', url: '' };
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return { description: parsed.description || '', url: parsed.url || '' };
            }
        } catch {
            // not JSON — treat the whole string as a legacy description
        }
        return { description: raw, url: '' };
    }
    return { description: '', url: '' };
};

export type InfoMode = 'description' | 'url' | null;

// Which info mode a parameter is in, from the persisted `info` string. Handles both the
// single-key shape ({"description":""} / {"url":""}) and any legacy {description,url}
// value, and returns null when there is no info. This lets a selected-but-blank field
// persist as a detectable mode so save-time validation can block it.
export const getInfoMode = (raw: any): InfoMode => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                const desc = (parsed.description || '').trim();
                const url = (parsed.url || '').trim();
                if (url) return 'url';
                if (desc) return 'description';
                if ('url' in parsed && !('description' in parsed)) return 'url';
                if ('description' in parsed && !('url' in parsed)) return 'description';
                return null;
            }
        } catch {
            // not JSON — legacy plain string
        }
        return 'description';
    }
    return null;
};

// Build the `info` string for a mode. ALWAYS a string; keeps the key present even when the
// value is blank so the mode persists (and save-time validation can catch an empty field).
export const buildInfo = (mode: 'description' | 'url', value: string): string =>
    mode === 'url' ? JSON.stringify({ url: value || '' }) : JSON.stringify({ description: value || '' });

// A URL is only rendered as a clickable link when it starts with http(s)://; otherwise
// it's shown as plain text.
export const isInfoUrl = (value: string): boolean =>
    typeof value === 'string' && /^https?:\/\//i.test(value.trim());
